import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAIBar, Message } from '../contexts/AIBarContext';
import { useTier } from './useTier';
import { API_BASE_URL } from '../services/api';

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  type?: 'app' | 'nutrition' | 'general';
  loggedFood?: boolean;
  meal?: any;
  mealType?: string;
  date?: string;
  mealSuggestions?: any[];
  loggedExercise?: boolean;
  exercise?: any;
}

interface PendingMealData {
  meal: any;
  mealType: string;
  date: string;
}

export const useAIChat = () => {
  const { user, updateUser } = useAuth();
  const { setPendingMeal, setPendingExercise, setCorrectionCount, addMessage, showUpgradeNudge } = useAIBar();
  const [isLoading, setIsLoading] = useState(false);
  const [pendingMealData, setPendingMealData] = useState<PendingMealData | null>(null);
  const lastMessageWasVoice = useRef(false);

  const { isPro } = useTier();

  // Helper to determine question type
  const determineQuestionType = (message: string): 'app' | 'nutrition' | 'general' => {
    const lowerMessage = message.toLowerCase();
    const appKeywords = ['how do i', 'how to', 'where is', 'find', 'navigate', 'use', 'feature'];
    const nutritionKeywords = [
      'log', 'food', 'meal', 'calories', 'protein', 'carbs', 'nutrition', 'eat', 'diet',
      'ate', 'had', 'drank', 'glass', 'cup', 'bowl', 'plate', 'piece', 'slice', 'serving',
      'breakfast', 'lunch', 'dinner', 'snack', 'recipe', 'cook', 'ingredient',
      'chicken', 'beef', 'fish', 'egg', 'milk', 'rice', 'pasta', 'bread', 'salad', 'fruit', 'vegetable',
      'supplement', 'vitamin', 'weight', 'macro',
    ];

    if (appKeywords.some(keyword => lowerMessage.includes(keyword))) {
      return 'app';
    }
    if (nutritionKeywords.some(keyword => lowerMessage.includes(keyword))) {
      return 'nutrition';
    }
    return 'general';
  };

  // Helper to get local date string
  const getLocalDateString = (date: Date): string => {
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  };

  // Text-to-speech fallback using browser API
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      const voices = speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => v.lang.startsWith('en-'));
      if (preferredVoice) utterance.voice = preferredVoice;
      utterance.rate = 1.02;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      speechSynthesis.speak(utterance);
    }
  };

  // Play OpenAI audio with fallback to browser TTS
  const playOpenAIAudio = async (base64Audio: string, fallbackText: string): Promise<void> => {
    let audioPlayedOrAttempted = false;

    const playFallback = () => {
      if (!audioPlayedOrAttempted) {
        audioPlayedOrAttempted = true;
        console.warn('TTS: OpenAI audio playback failed, falling back to browser TTS');
        speakText(fallbackText);
      }
    };

    try {
      const audioBlob = new Blob([
        Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0))
      ], { type: 'audio/mpeg' });

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        audioPlayedOrAttempted = true;
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = (error) => {
        console.warn('TTS: OpenAI audio playback error', error);
        URL.revokeObjectURL(audioUrl);
        playFallback();
      };

      await audio.play();
    } catch (error) {
      console.error('TTS: Critical error in playOpenAIAudio', error);
      playFallback();
    }
  };

  // Send message to AI
  const sendMessage = useCallback(async (
    messageText: string,
    conversationHistory: ChatMessage[],
    enableTTS: boolean = false
  ): Promise<ChatMessage | null> => {
    setIsLoading(true);

    try {
      const questionType = determineQuestionType(messageText);
      const response = await fetch(`${API_BASE_URL}/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message: messageText,
          type: questionType,
          conversationHistory: conversationHistory.slice(-12),
          clientDate: getLocalDateString(new Date()),
          tts: enableTTS,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          // Surface the upgrade modal at the exact moment of denial —
          // a plain text bubble converts nobody
          if (errorData.upgradeRequired) {
            showUpgradeNudge({
              usageType: 'assistant',
              title: errorData.limitType === 'monthly' ? 'Monthly Allowance Reached' : 'Daily Limit Reached',
              message: errorData.error,
            });
          }
          throw new Error(errorData.error || 'You have exceeded your usage limit. Please upgrade to Pro.');
        }
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: data.response,
        isUser: false,
        timestamp: new Date(),
        type: questionType,
        loggedFood: data.loggedFood,
        meal: data.meal,
        mealType: data.mealType,
        date: data.date,
        mealSuggestions: data.mealSuggestions,
        loggedExercise: data.loggedExercise,
        exercise: data.exercise,
      };

      // Update user usage count
      if (user && updateUser) {
        const updatedUser = {
          ...user,
          usage: {
            ...user.usage,
            aiAssistantMessages: user.usage.aiAssistantMessages + 1,
            ...(data.loggedFood ? { aiFoodQueries: user.usage.aiFoodQueries + 1 } : {})
          }
        };
        await updateUser(updatedUser);
      }

      // Notify other components if food was logged
      if (data.loggedFood) {
        window.dispatchEvent(new CustomEvent('foodLogged', { detail: { date: getLocalDateString(new Date()) } }));
      }

      // Play TTS if enabled and voice was used (fire-and-forget so UI updates immediately)
      if (enableTTS || lastMessageWasVoice.current) {
        if (data.audio && isPro) {
          playOpenAIAudio(data.audio, data.response).catch(() => {});
        } else {
          speakText(data.response);
        }
        lastMessageWasVoice.current = false;
      }

      // Centralized pending state management — handles ALL input modes (text, voice, future)
      // These set shared context state directly so ExpandedAIChat doesn't need to extract from return values
      const today = getLocalDateString(new Date());

      if (!data.loggedFood && (data.foods || data.meal)) {
        const foods = data.foods || [data.meal];
        const hour = new Date().getHours();
        const defaultMealType = hour < 10 ? 'breakfast' : hour < 15 ? 'lunch' : hour < 17 ? 'snack' : hour < 22 ? 'dinner' : 'snack';
        setPendingMeal({
          foods,
          meal: foods[0],
          mealType: data.mealType || defaultMealType,
          date: data.date || today,
        });
        setCorrectionCount(0);
      }

      if (data.loggedExercise === false && data.exercise) {
        setPendingExercise({
          exercise: data.exercise,
          date: data.date || today,
        });
        setCorrectionCount(0);
      }

      // Add AI message to chat context so it appears in the conversation
      addMessage({
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        type: questionType,
        mealSuggestions: data.mealSuggestions,
      });

      return aiMessage;
    } catch (error: any) {
      console.error('Error sending message:', error);
      addMessage({
        role: 'assistant',
        content: error.message || "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
        type: 'general',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, updateUser, isPro, setPendingMeal, setPendingExercise, setCorrectionCount, addMessage, showUpgradeNudge]);

  // Send message from voice transcript
  const sendVoiceMessage = useCallback(async (
    transcript: string,
    conversationHistory: ChatMessage[]
  ): Promise<ChatMessage | null> => {
    lastMessageWasVoice.current = true;
    return sendMessage(transcript, conversationHistory, true);
  }, [sendMessage]);

  // Get meal suggestions
  const getMealSuggestions = useCallback(async (
    conversationHistory: ChatMessage[]
  ): Promise<{ message: ChatMessage; suggestions?: any[] }> => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message: 'meal suggestions',
          type: 'nutrition',
          conversationHistory: conversationHistory.slice(-12),
          clientDate: getLocalDateString(new Date()),
          tts: false,
        }),
      });

      if (!response.ok) throw new Error('Failed to get meal suggestions');
      const data = await response.json();

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: data.response,
        isUser: false,
        timestamp: new Date(),
        type: 'nutrition',
      };

      // Play audio if available (fire-and-forget)
      if (data.audio && isPro) {
        playOpenAIAudio(data.audio, data.response).catch(() => {});
      }

      return {
        message: aiMessage,
        suggestions: data.mealSuggestions,
      };
    } catch (error: any) {
      console.error('Error getting meal suggestions:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, I couldn't get meal suggestions right now. Please try again.",
        isUser: false,
        timestamp: new Date(),
        type: 'general',
      };
      return { message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [isPro]);

  // Log a meal
  const logMeal = useCallback(async (mealDescription: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message: `log ${mealDescription}`,
          type: 'nutrition',
          conversationHistory: [],
          clientDate: getLocalDateString(new Date()),
        }),
      });

      if (!response.ok) throw new Error('Failed to log meal');
      const data = await response.json();

      if (data.loggedFood) {
        window.dispatchEvent(new CustomEvent('foodLogged', { detail: { date: getLocalDateString(new Date()) } }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error logging meal:', error);
      return false;
    }
  }, []);

  // Correct a pending meal from photo analysis (itemized foods array)
  const correctMeal = useCallback(async (
    correction: string,
    currentFoods: any[],
    mealType: string,
    date: string,
  ): Promise<{ response: string; foods: any[]; meal: any; mealType: string; date: string; corrected: boolean } | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/photo/correct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ correction, currentFoods, mealType, date }),
      });

      if (!response.ok) throw new Error('Correction failed');
      return await response.json();
    } catch (error) {
      console.error('Error correcting meal:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Correct a pending exercise from AI analysis
  const correctExercise = useCallback(async (
    correction: string,
    currentExercise: any,
  ): Promise<{ response: string; exercise: any; corrected: boolean } | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/exercise/ai-correct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ correction, currentExercise }),
      });

      if (!response.ok) throw new Error('Correction failed');
      return await response.json();
    } catch (error) {
      console.error('Error correcting exercise:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // End chat session (triggers summary + memory extraction)
  const endSession = useCallback(async (
    sessionId: string | null,
    messages: Array<{ role: string; content: string }>,
  ): Promise<void> => {
    if (messages.length < 2) return;
    try {
      await fetch(`${API_BASE_URL}/ai-assistant/session/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ sessionId, messages }),
        keepalive: true,
      });
    } catch (error) {
      console.error('Error ending session:', error);
    }
  }, []);

  // Save chat session
  const saveSession = useCallback(async (
    messages: Array<{ role: string; content: string; timestamp?: Date; type?: string; photoUrl?: string }>,
    sessionId?: string | null,
  ): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/session/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ messages, sessionId }),
        keepalive: true,
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.sessionId || null;
    } catch (error) {
      console.error('Error saving session:', error);
      return null;
    }
  }, []);

  // Load the latest session for resume
  const loadLastSession = useCallback(async (): Promise<{
    sessionId: string;
    messages: Array<{ role: string; content: string; timestamp: string; type?: string; photoUrl?: string }>;
    summary: string;
    status: string;
  } | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-assistant/session/latest`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) return null;
      const data = await response.json();
      return data.session || null;
    } catch (error) {
      console.error('Error loading last session:', error);
      return null;
    }
  }, []);

  return {
    isLoading,
    sendMessage,
    sendVoiceMessage,
    getMealSuggestions,
    logMeal,
    pendingMealData,
    setPendingMealData,
    correctMeal,
    correctExercise,
    endSession,
    saveSession,
    loadLastSession,
  };
};
